import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./ProductDetail.scss";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";
import { RootState, AppDispatch } from "../../store";
import {
  MotifIcon,
  MotifTabNavigation,
  MotifTabControl,
  MotifAccordion,
  MotifAccordionTrigger,
  MotifAccordionContent,
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifCheckbox,
  MotifAvatar,
  MotifInput,
  MotifSelect,
  MotifOption,
  MotifToast,
  MotifTruncate,
  MotifTooltip,
  MotifErrorMessage,
  MotifChip,
} from "@ey-xd/motif-react";
import {
  IconoirArrowLeft,
  IconoirArrowRight,
  IconoirUser,
  IconoirNotes,
  IconoirLink,
  IconoirOpenNewWindow,
  IconoirDownload,
} from "@ey-xd/motif-icon";
import Loader from "../../components/Loader";
import BreadcrumbHome from "../../components/BreadcrumbHome";
import BundleCard from "../../components/BundleCard";
import {
  fetchProductDetailsData,
  resetProductDetailState,
} from "../../store/slices/CTProductDetailsSlice";
import {
  fetchSkuInformationData,
  resetSkuInformationState,
} from "../../store/slices/CTSkuInformationSlice";
import { fetchSkuListData } from "../../store/slices/CTSkuListSlice";
import { fetchDraftOrdersListData } from "../../store/slices/CTDraftOrdersListSlice";
import SearchInput from "../../components/SearchInput/SearchInput";
import CustomPagination from "../../components/CustomPagination";
import {
  isEmpty,
  isStringHavingSpecialChars,
  isValidUrl,
} from "../../utils/Validator";
import { useNavigate } from "react-router";
import { addToOrderBundle } from "../../store/slices/CTAddToOrderBundleSlice";
import productPreviewTickIcon from "../../assets/images/product_preview_tick_icon.svg";
import productListEmptyImg from "../../assets/images/empty_product_list_default.svg";
import {
  containsEmoji,
  downloadBase64File,
  formattingBytes,
  setOrderId,
  urlify,
} from "../../utils/common";
import NumericInput from "../../components/NumericInput/NumericInput";
import { fetchSkuConfigData } from "../../store/slices/CTDBSkuConfigSlice";
import {
  MAX_QUANTITY,
  MAX_DURATION_MONTHS,
  MAX_DURATION_WEEKS,
  OVERVIEW,
  PRODUCT_SKUS_INFORMATION,
  MORE_INFORMATION,
  CONTACTS,
  COST_MACRO_CATEGORY,
  EMOJI_ERROR_MESSAGE,
  EMOJI_ERROR_MESSAGE_CREATE,
  RESOURCES,
  ERROR_RETRIVING_DATA,
} from "../../Constant";
import { COST_INTERVAL_CODE } from "./Constants";
import CheckBoxToolTip from "./CheckBoxToolTip";
import NoSearchResult from "../../components/NoSearchResult/NoSearchResult";
import {
  fetchArtifactDetailsData,
  fetchArtifactFileData,
  resetArtifactDetailState,
} from "../../store/slices/CTArtifactDetailsSlice";
import OrderItemsHeaderTooltip from "../../components/OrderPipeline/OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";
import { fixedCostInfo, variableCostInfo } from "../../utils/skuTable";
import { metricUnitMapping } from "../Checkout/CheckoutHelper";
import NotFound from "../../components/HttpError/NotFound";
import ErrorOrderPage from "../../components/ErrorOrderPage/ErrorOrderPage";

interface ArtifactType {
  ProductArtifactId: string;
  ProductId: string;
  Title: string;
  Description: string;
  URL: string;
  ArtifactTypeCd: string;
  ArtifactCategoryCd: string;
  IsActive: boolean;
  CreatedUser: string;
  UpdatedUser: string;
}

const ProductDetail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const param = useParams();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [paginationObj, setPaginationObj] = useState<any>({
    currentPage: 1,
    pageSize: 10,
    searchText: "",
    SortOrder: "",
    SortColumn: "",
  });

  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState({
    skuId: "",
    skuName: "",
    skuDescription: "",
    skuCommercialStatus: "",
    skuCost: 0,
    returnable: false,
    skuCostInterval: "",
    skuCostMetricNSR: 0,
    skuCostMetricCd: "",
    skuCostMacroCategoryCd: "",
    SkuIndexation: 0,
  });

  const [skuListViewData, setSkuListViewData] = useState<any>([]);
  const [selectedSkuList, setSelectedSkuList] = useState<any>([]);

  const [isCancelSelectionOpen, setIsCancelSelectionOpen] = useState(false);
  const [isAddToOrderBundleOpen, setIsAddToOrderBundleOpen] = useState(false);
  const [orderBundleName, setOrderBundleName] = useState("");
  const [isNewOrderBundle, setIsNewOrderBundle] = useState(true);
  const [orderBundleDropdownValue, setOrderBundleDropdownValue] = useState("");
  const [newOrderBundleError, setNewOrderBundleError] = useState("");
  const [existingOrderBundleError, setExistingOrderBundleError] = useState("");
  const [toastError, setToastError] = useState("");
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");
  const [fileLoading, setFileLoading] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const isProductDataLoading = useSelector(
    (state: RootState) => state.CTProductDetailsSlice.isLoading,
  );
  const productData = useSelector(
    (state: RootState) => state.CTProductDetailsSlice.data,
  );
  const errorProductData = useSelector(
    (state: RootState) => state.CTProductDetailsSlice.error,
  );

  const isArtifactDataLoading = useSelector(
    (state: RootState) => state.CTArtifactDetailsSlice.isLoading,
  );
  const artifactData = useSelector(
    (state: RootState) => state.CTArtifactDetailsSlice.data,
  );
  const errorArtifactData = useSelector(
    (state: RootState) => state.CTArtifactDetailsSlice.error,
  );

  const isSkuInformationLoading = useSelector(
    (state: RootState) => state.CTSkuInformationSlice.isLoading,
  );
  const skuInformationData = useSelector(
    (state: RootState) => state.CTSkuInformationSlice.data,
  );
  const errorSkuInformation = useSelector(
    (state: RootState) => state.CTSkuInformationSlice.error,
  );

  const isSkuListLoading = useSelector(
    (state: RootState) => state.CTSkuListSlice.isLoading,
  );
  const skuListData = useSelector(
    (state: RootState) => state.CTSkuListSlice.data,
  );
  const errorSkuList = useSelector(
    (state: RootState) => state.CTSkuListSlice.error,
  );

  const isDraftOrdersListLoading = useSelector(
    (state: RootState) => state.CTDraftOrdersListSlice.isLoading,
  );
  const draftOrdersListData = useSelector(
    (state: RootState) => state.CTDraftOrdersListSlice.data,
  );
  // this is to fetch the cost metric and cost type fields
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );

  // const errorDraftOrdersList = useSelector(
  //   (state: RootState) => state.CTDraftOrdersListSlice.error
  // );

  useEffect(() => {
    if (param.id !== undefined) {
      dispatch(
        fetchSkuListData({
          search: paginationObj.searchText,
          productId: param.id,
          page: paginationObj.currentPage,
          record: paginationObj.pageSize,
          sortOrder: paginationObj.SortOrder,
          sortColumn: paginationObj.SortColumn,
        }),
      );
    }
  }, [dispatch, param.id, paginationObj, paginationObj.searchText]);

  useEffect(() => {
    if (
      productData &&
      productData !== null &&
      param.id !== productData?.Data?.ProductId
    ) {
      dispatch(resetProductDetailState());
      dispatch(resetSkuInformationState());
    }
    if (param.id !== undefined && productData === null) {
      dispatch(
        fetchProductDetailsData({
          productId: param.id,
        }),
      );
      dispatch(
        fetchSkuInformationData({
          ProductId: param.id,
        }),
      );
    }
  }, [dispatch, param.id, productData]);

  useEffect(() => {
    if (
      artifactData &&
      artifactData !== null &&
      param.id !== productData?.Data?.ProductId
    ) {
      dispatch(resetArtifactDetailState());
    }
    if (param.id !== undefined && productData === null) {
      dispatch(
        fetchArtifactDetailsData({
          productId: param.id,
        }),
      );
    }
  }, [dispatch, param.id, artifactData]);

  useEffect(() => {
    dispatch(fetchDraftOrdersListData());
  }, []);

  useLayoutEffect(() => {
    const isCurrentProduct = param.id === productData?.Data?.ProductId;
    if (!isCurrentProduct) {
      setShowReadMore(false);
      setIsDescriptionExpanded(false);
      return;
    }

    if (descriptionRef.current) {
      const descriptionElement = descriptionRef.current;
      const shouldShowReadMore =
        descriptionElement.scrollHeight - descriptionElement.clientHeight > 1;

      setShowReadMore(shouldShowReadMore);
      if (!shouldShowReadMore) {
        setIsDescriptionExpanded(false);
      }
      return;
    }

    setShowReadMore(false);
    setIsDescriptionExpanded(false);
  }, [
    param.id,
    productData?.Data?.ProductId,
    productData?.Data?.CommercialDescription,
  ]);

  useEffect(() => {
    if (param.id !== undefined && param.id !== null && configData === null) {
      dispatch(fetchSkuConfigData());
    }
  }, [dispatch, param.id, configData]);

  useEffect(() => {
    if (skuListData && skuListData !== null) {
      setSkuListViewData(
        skuListData.Result.map((sku: any) => {
          const selectedSku = selectedSkuList.filter(
            (selectedSku: any) => selectedSku.SkuId === sku.SkuId,
          );
          return {
            ...sku,
            Quantity:
              selectedSku.length > 0
                ? selectedSku[0].Quantity
                : sku.CostIntervalCd == "onetime"
                  ? 1
                  : "",
            QuantityError:
              selectedSku.length > 0 ? selectedSku[0].QuantityError : "",
            IsSelected:
              selectedSku.length > 0 ? selectedSku[0].IsSelected : false,
            Duration:
              selectedSku.length > 0
                ? selectedSku[0].Duration
                : sku.CostIntervalCd == "onetime"
                  ? 1
                  : "",
            DurationError:
              selectedSku.length > 0 ? selectedSku[0].DurationError : "",
            TotalCost: 0,
            SkuRelation: sku.SkuRelation.map((relation: any) => {
              const selectedRelation =
                selectedSku.length > 0
                  ? selectedSku[0].SkuRelation.filter(
                      (relationSku: any) =>
                        relationSku.SkuId === relation.SkuId,
                    )
                  : [];
              return {
                ...relation,
                Quantity:
                  selectedRelation.length > 0
                    ? selectedRelation[0].Quantity
                    : relation.CostIntervalCd == "onetime"
                      ? 1
                      : "",
                QuantityError:
                  selectedRelation.length > 0
                    ? selectedRelation[0].QuantityError
                    : "",
                Duration:
                  selectedRelation.length > 0
                    ? selectedRelation[0].Duration
                    : relation.CostIntervalCd == "onetime"
                      ? 1
                      : "",
                DurationError:
                  selectedRelation.length > 0
                    ? selectedRelation[0].DurationError
                    : "",
                TotalCost: 0,
              };
            }),
          };
        }),
      );
    }
  }, [skuListData]);

  // finding the mapping details for cost metric and cost type and macro category
  let macroCategory = "";
  if (configData?.CostMacroCategory) {
    macroCategory =
      (configData?.CostMacroCategory ?? [])
        .filter((item) =>
          viewDetailsData.skuCostMacroCategoryCd.includes(
            item.CostMacroCategoryCd,
          ),
        )
        .map((item) => item.Name)
        .join(", ") || "";
  }

  const costType =
    configData?.CostCategory.find(
      (item) => item.CostMetricCd === viewDetailsData.skuCostMetricCd,
    )?.CostMetricType || "";
  const costMetric =
    configData?.CostCategory.find(
      (item) => item.CostMetricCd === viewDetailsData.skuCostMetricCd,
    )?.CostMetric || "";

  const onTabChange = (key: number) => {
    setSelectedTab(key);
  };

  const handleViewDetails = (
    skuId: string,
    skuName: string,
    description: string,
    commercialStatus: string,
    cost: number,
    returnable: boolean,
    costInterval: string,
    CostMetricNSR: number,
    CostMetricCd: string,
    CostMacroCategoryCd: string,
    Indexation: number,
  ) => {
    setIsViewDetailsOpen(true);
    setViewDetailsData({
      skuId: skuId,
      skuName: skuName,
      skuDescription: description,
      skuCommercialStatus: commercialStatus,
      skuCost: cost,
      returnable: returnable,
      skuCostInterval: costInterval,
      skuCostMetricNSR: CostMetricNSR,
      skuCostMetricCd: CostMetricCd,
      skuCostMacroCategoryCd: CostMacroCategoryCd,
      SkuIndexation: Indexation,
    });
  };

  const handleCloseViewDetails = () => {
    setIsViewDetailsOpen(false);
    document.body.style.overflow = "";
  };

  const handleSearch = (searchText: string) => {
    setPaginationObj({
      ...paginationObj,
      currentPage: 1,
      pageSize: Number(paginationObj.pageSize),
      searchText: searchText,
    });
  };

  const resetPage = () => {
    setPaginationObj({
      ...paginationObj,
      currentPage: 1,
      pageSize: Number(paginationObj.pageSize),
      searchText: "",
    });
  };

  const handleCancelSelection = () => {
    if (selectedSkuList.length === 0) {
      return;
    }
    setIsCancelSelectionOpen(true);
  };

  const handleCancelSelectionLeave = () => {
    setIsCancelSelectionOpen(false);
    document.body.style.overflow = "";
    navigate("/marketplace");
  };

  const handleCancelSelectionContinue = () => {
    setIsCancelSelectionOpen(false);
    document.body.style.overflow = "";
  };

  const isFixedCostSku = (sku: any) => {
    return sku.CostMetricCd === "fix_cost" || sku.CostIntervalCd === "onetime";
  };

  const handleAddToOrderBundle = () => {
    let isError = false;

    for (const sku of selectedSkuList) {
      if (sku.IsSelected) {
        const maxQty = MAX_QUANTITY;
        const maxDuration =
          sku.CostIntervalCd === "month"
            ? MAX_DURATION_MONTHS
            : sku.CostIntervalCd === "week"
              ? MAX_DURATION_WEEKS
              : MAX_QUANTITY;
        if (
          sku.Quantity === "" ||
          sku.Quantity <= "0" ||
          Number(sku.Quantity) > maxQty ||
          sku.Duration === "" ||
          sku.Duration <= "0" ||
          Number(sku.Duration) < sku.CostIntervalMin ||
          Number(sku.Duration) > maxDuration ||
          sku.QuantityError !== "" ||
          sku.DurationError !== ""
        ) {
          isError = true;
          break;
        } else {
          isError = false;
        }

        for (const relation of sku.SkuRelation) {
          const relMaxQty = MAX_QUANTITY;
          const relMaxDuration =
            relation.CostIntervalCd === "month"
              ? MAX_DURATION_MONTHS
              : relation.CostIntervalCd === "week"
                ? MAX_DURATION_WEEKS
                : MAX_QUANTITY;
          if (
            relation.Quantity === "" ||
            relation.Quantity <= "0" ||
            Number(relation.Quantity) > relMaxQty ||
            relation.Duration === "" ||
            relation.Duration <= "0" ||
            Number(relation.Duration) < relation.CostIntervalMin ||
            Number(relation.Duration) > relMaxDuration ||
            relation.QuantityError !== "" ||
            relation.DurationError !== ""
          ) {
            isError = true;
            break;
          } else {
            isError = false;
          }
        }
      }
    }

    if (isError) {
      setToastError(
        "Please check the quantity and duration of the selected SKUs.",
      );
      setTimeout(() => {
        if (toastError !== "") {
          setToastError("");
        }
      }, 3000);
    } else {
      setIsAddToOrderBundleOpen(true);
    }
  };

  const handleAddToOrderBundleConfirm = async () => {
    const OrderSku = selectedSkuList
      .filter((sku: any) => sku.IsSelected)
      .map((sku: any) => {
        return {
          SkuId: sku.SkuId,
          Quantity: Number(sku.Quantity),
          Intervals: Number(sku.Duration),
        };
      });

    const OrderSkuRelation = selectedSkuList
      .filter((sku: any) => sku.IsSelected)
      .map((sku: any) =>
        sku.SkuRelation.map((relation: any) => {
          return {
            ParentSkuId: sku.SkuId,
            SkuId: relation.SkuId,
            Quantity: Number(relation.Quantity),
            Intervals: Number(relation.Duration),
          };
        }),
      );

    if (OrderSkuRelation.flat().filter(Boolean).length > 0) {
      OrderSku.push(...OrderSkuRelation.flat());
    }

    if (isNewOrderBundle) {
      const description = orderBundleName.trim();

      if (description === "") {
        setNewOrderBundleError("Please enter a valid order bundle name.");
        return;
      } else if (
        draftOrdersListData &&
        draftOrdersListData.filter((order) => order.Description === description)
          .length > 0
      ) {
        setNewOrderBundleError(
          "This name is already taken, Please choose a different name",
        );
        return;
      }

      try {
        const response = await dispatch(
          addToOrderBundle({
            Action: "createDraft",
            Description: description,
            OrderSku: OrderSku,
          }),
        );
        if (response.payload.Status === "success") {
          setOrderId(response.payload.Data.OrderId);
          document.body.style.overflow = "";
          navigate("/checkout");
        } else if (response.payload.message) {
          setNewOrderBundleError(response.payload.message);
        } else {
          setNewOrderBundleError(
            "Error while creating order bundle, please try again later.",
          );
        }
      } catch (error) {
        setIsAddToOrderBundleOpen(false);
        setToastError(
          "Error while adding SKUs to order bundle, please try again later.",
        );
        setTimeout(() => {
          if (toastError !== "") {
            setToastError("");
          }
        }, 5000);
        console.error("Error creating order bundle:", error);
      }
    } else {
      try {
        if (orderBundleDropdownValue === "") {
          setExistingOrderBundleError("Please select an order bundle.");
          return;
        }

        const selectedOrderBundle = draftOrdersListData?.find(
          (order) => order.Description === orderBundleDropdownValue,
        );

        if (isSelectedSKUsInOrderBundle(OrderSku, selectedOrderBundle)) {
          setExistingOrderBundleError(
            "Selected SKUs, can’t be added to order bundle, it already exist within the bundle",
          );
          return;
        }

        const response = await dispatch(
          addToOrderBundle({
            Action: "updateDraft",
            Description: orderBundleDropdownValue,
            OrderSku: OrderSku,
            OrderId: draftOrdersListData?.filter(
              (order) => order.Description === orderBundleDropdownValue,
            )[0]?.OrderParentId,
            EngagementId: draftOrdersListData?.filter(
              (order) => order.Description === orderBundleDropdownValue,
            )[0]?.EngagementId,
            OpportunityId: draftOrdersListData?.filter(
              (order) => order.Description === orderBundleDropdownValue,
            )[0]?.OpportunityId,
          }),
        );
        if (response.payload.Status === "success") {
          setOrderId(response.payload.Data.OrderId);
          document.body.style.overflow = "";
          navigate("/checkout");
        } else if (response.payload.message) {
          setExistingOrderBundleError(response.payload.message);
        } else {
          setExistingOrderBundleError(
            "Error while updating order bundle, please try again later.",
          );
        }
      } catch (error) {
        setIsAddToOrderBundleOpen(false);
        setToastError(
          "Error while adding SKUs to order bundle, please try again later.",
        );
        setTimeout(() => {
          if (toastError !== "") {
            setToastError("");
          }
        }, 5000);
        console.error("Error updating order bundle:", error);
      }
    }
  };

  const handleSelectDraftOrderBundle = (val: any) => {
    setOrderBundleDropdownValue(val);
    setExistingOrderBundleError("");

    const OrderSku = selectedSkuList
      .filter((sku: any) => sku.IsSelected)
      .map((sku: any) => {
        return {
          SkuId: sku.SkuId,
          Quantity: Number(sku.Quantity),
          Intervals: Number(sku.Duration),
        };
      });

    const selectedOrderBundle = draftOrdersListData?.find(
      (order) => order.Description === val,
    );

    if (isSelectedSKUsInOrderBundle(OrderSku, selectedOrderBundle)) {
      setExistingOrderBundleError(
        "Selected SKUs, can’t be added to order bundle, it already exist within the bundle",
      );
      return;
    }
  };

  const isSelectedSKUsInOrderBundle = (
    OrderSKU: any[],
    selectedOrderBundle: any,
  ) => {
    const orderSKUIds = OrderSKU.map((sku) => {
      if (!sku.ParentSkuId) return sku.SkuId;
    }).filter(Boolean);
    const selectedOrderBundleSKUs =
      selectedOrderBundle?.Details.map((product: any) =>
        product.SkuDetails.map((sku: any) => sku.SkuId),
      ).flat() || [];

    const hasCommonSKU = orderSKUIds.some((skuId) =>
      selectedOrderBundleSKUs.includes(skuId),
    );

    return hasCommonSKU;
  };

  const handleAddToOrderBundleCancel = () => {
    setIsAddToOrderBundleOpen(false);
    setOrderBundleName("");
    setOrderBundleDropdownValue("");
    setIsNewOrderBundle(true);
    setNewOrderBundleError("");
    setExistingOrderBundleError("");
    document.body.style.overflow = "";
  };

  const selectNewOrderBundleOption = () => {
    setIsNewOrderBundle(true);
    setOrderBundleDropdownValue("");
    setExistingOrderBundleError("");
  };

  const selectExistingOrderBundleOption = () => {
    setIsNewOrderBundle(false);
    setOrderBundleName("");
    setNewOrderBundleError("");
  };

  const handleSelectChange = (skuId: string) => {
    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId && sku.IsSelected) {
        return {
          ...sku,
          IsSelected: !sku.IsSelected,
          Quantity: sku.CostIntervalCd == "onetime" ? 1 : "",
          QuantityError: "",
          Duration: sku.CostIntervalCd == "onetime" ? 1 : "",
          DurationError: "",
          SkuRelation: sku.SkuRelation.map((relation: any) => {
            return {
              ...relation,
              Quantity: relation.CostIntervalCd == "onetime" ? 1 : "",
              QuantityError: "",
              Duration: relation.CostIntervalCd == "onetime" ? 1 : "",
              DurationError: "",
            };
          }),
        };
      } else if (sku.SkuId === skuId && !sku.IsSelected) {
        return {
          ...sku,
          IsSelected: !sku.IsSelected,
          Quantity: 1,
          Duration:
            sku.CostIntervalMin && sku.CostIntervalMin != "0"
              ? sku.CostIntervalMin
              : "1",
          QuantityError: "",
          DurationError: "",
          SkuRelation: sku.SkuRelation.map((relation: any) => {
            return {
              ...relation,
              Quantity: 1,
              QuantityError: "",
              Duration:
                relation.CostIntervalMin && relation.CostIntervalMin != "0"
                  ? relation.CostIntervalMin
                  : "1",
              DurationError: "",
            };
          }),
        };
      }
      return sku;
    });

    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId && sku.IsSelected) {
          return {
            ...sku,
            IsSelected: !sku.IsSelected,
            Quantity: sku.CostIntervalCd == "onetime" ? 1 : "",
            QuantityError: "",
            Duration: sku.CostIntervalCd == "onetime" ? 1 : "",
            DurationError: "",
            SkuRelation: sku.SkuRelation.map((relation: any) => {
              return {
                ...relation,
                Quantity: relation.CostIntervalCd == "onetime" ? 1 : "",
                QuantityError: "",
                Duration: relation.CostIntervalCd == "onetime" ? 1 : "",
                DurationError: "",
              };
            }),
          };
        } else if (sku.SkuId === skuId && !sku.IsSelected) {
          return {
            ...sku,
            IsSelected: !sku.IsSelected,
            Quantity: 1,
            Duration:
              sku.CostIntervalMin && sku.CostIntervalMin != "0"
                ? sku.CostIntervalMin
                : "1",
            QuantityError: "",
            DurationError: "",
            SkuRelation: sku.SkuRelation.map((relation: any) => {
              return {
                ...relation,
                Quantity: 1,
                QuantityError: "",
                Duration:
                  relation.CostIntervalMin && relation.CostIntervalMin != "0"
                    ? relation.CostIntervalMin
                    : "1",
                DurationError: "",
              };
            }),
          };
        }

        return sku;
      });
      setSelectedSkuList(updatedSkuList.filter((sku: any) => sku.IsSelected));
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleSelectAllChange = () => {
    const selectableSkus = skuListViewData.filter(
      (sku: any) =>
        sku.CostIntervalCd?.toLowerCase() !== COST_INTERVAL_CODE.auto_transact,
    );

    if (selectableSkus.length === 0) {
      return;
    }

    const shouldSelectAll = !selectableSkus.every((sku: any) => sku.IsSelected);

    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      const isDisabled =
        sku.CostIntervalCd?.toLowerCase() === COST_INTERVAL_CODE.auto_transact;

      if (isDisabled) {
        return sku;
      }

      return {
        ...sku,
        IsSelected: shouldSelectAll,
        Quantity: shouldSelectAll
          ? 1
          : sku.CostIntervalCd == "onetime"
            ? 1
            : "",
        QuantityError: "",
        Duration: shouldSelectAll
          ? sku.CostIntervalMin && sku.CostIntervalMin != "0"
            ? sku.CostIntervalMin
            : "1"
          : sku.CostIntervalCd == "onetime"
            ? 1
            : "",
        DurationError: "",
        SkuRelation: sku.SkuRelation.map((relation: any) => {
          return {
            ...relation,
            Quantity: shouldSelectAll
              ? 1
              : relation.CostIntervalCd == "onetime"
                ? 1
                : "",
            QuantityError: "",
            Duration: shouldSelectAll
              ? relation.CostIntervalMin && relation.CostIntervalMin != "0"
                ? relation.CostIntervalMin
                : "1"
              : relation.CostIntervalCd == "onetime"
                ? 1
                : "",
            DurationError: "",
          };
        }),
      };
    });

    setSkuListViewData(updatedSkuListViewData);

    const currentPageSkuIdSet = new Set(
      updatedSkuListViewData.map((sku: any) => sku.SkuId),
    );
    const retainedSelections = selectedSkuList.filter(
      (sku: any) => !currentPageSkuIdSet.has(sku.SkuId) && sku.IsSelected,
    );
    const currentPageSelections = updatedSkuListViewData.filter(
      (sku: any) => sku.IsSelected,
    );

    setSelectedSkuList([...retainedSelections, ...currentPageSelections]);
  };

  const handleQuantityChange = (skuId: string, quantity: string) => {
    const currentSku = skuListViewData.find((sku: any) => sku.SkuId === skuId);
    const shouldAutoSelect = quantity !== "" && !currentSku?.IsSelected;

    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        return {
          ...sku,
          IsSelected: shouldAutoSelect ? true : sku.IsSelected,
          Quantity: quantity,
          QuantityError:
            quantity === "" ? "Required" : Number(quantity) <= 0 ? "Min 1" : "",
          Duration:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? sku.CostIntervalMin && sku.CostIntervalMin != "0"
                ? sku.CostIntervalMin
                : "1"
              : sku.Duration,
          DurationError:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? ""
              : sku.DurationError,
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          return {
            ...sku,
            Quantity: quantity,
            QuantityError:
              quantity === ""
                ? "Required"
                : Number(quantity) <= 0
                  ? "Min 1"
                  : "",
          };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleRelationQuantityChange = (
    skuId: string,
    skuRelationId: string,
    quantity: string,
  ) => {
    const currentSku = skuListViewData.find((sku: any) => sku.SkuId === skuId);
    const shouldAutoSelect = quantity !== "" && !currentSku?.IsSelected;

    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        const updatedSkuRelation = sku.SkuRelation.map((relation: any) => {
          if (relation.SkuId === skuRelationId) {
            return {
              ...relation,
              Quantity: quantity,
              QuantityError:
                quantity === ""
                  ? "Required"
                  : Number(quantity) <= 0
                    ? "Min 1"
                    : "",
            };
          }
          return relation;
        });
        return {
          ...sku,
          IsSelected: shouldAutoSelect ? true : sku.IsSelected,
          Quantity: shouldAutoSelect
            ? sku.Quantity === ""
              ? 1
              : sku.Quantity
            : sku.Quantity,
          QuantityError: shouldAutoSelect ? "" : sku.QuantityError,
          Duration:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? sku.CostIntervalMin && sku.CostIntervalMin != "0"
                ? sku.CostIntervalMin
                : "1"
              : sku.Duration,
          DurationError:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? ""
              : sku.DurationError,
          SkuRelation: updatedSkuRelation,
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          const updatedSkuRelation = sku.SkuRelation.map((relation: any) => {
            if (relation.SkuId === skuRelationId) {
              return {
                ...relation,
                Quantity: quantity,
                QuantityError:
                  quantity === ""
                    ? "Required"
                    : Number(quantity) <= 0
                      ? "Min 1"
                      : "",
              };
            }
            return relation;
          });
          return { ...sku, SkuRelation: updatedSkuRelation };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleDurationChange = (skuId: string, duration: string) => {
    const currentSku = skuListViewData.find((sku: any) => sku.SkuId === skuId);
    const shouldAutoSelect = duration !== "" && !currentSku?.IsSelected;

    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        return {
          ...sku,
          IsSelected: shouldAutoSelect ? true : sku.IsSelected,
          Quantity:
            shouldAutoSelect &&
            (sku.Quantity === "" || sku.Quantity === undefined)
              ? 1
              : sku.Quantity,
          QuantityError:
            shouldAutoSelect &&
            (sku.Quantity === "" || sku.Quantity === undefined)
              ? ""
              : sku.QuantityError,
          Duration: duration,
          DurationError:
            duration == ""
              ? "Required"
              : Number(duration) < sku.CostIntervalMin
                ? "Min " +
                  sku.CostIntervalCd +
                  "s '" +
                  sku.CostIntervalMin +
                  "'"
                : "",
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          return {
            ...sku,
            Duration: duration,
            DurationError:
              duration == ""
                ? "Required"
                : Number(duration) < sku.CostIntervalMin
                  ? "Min " +
                    sku.CostIntervalCd +
                    " '" +
                    sku.CostIntervalMin +
                    "'"
                  : "",
          };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const handleRelationDurationChange = (
    skuId: string,
    skuRelationId: string,
    duration: string,
  ) => {
    const currentSku = skuListViewData.find((sku: any) => sku.SkuId === skuId);
    const shouldAutoSelect = duration !== "" && !currentSku?.IsSelected;

    const updatedSkuListViewData = skuListViewData.map((sku: any) => {
      if (sku.SkuId === skuId) {
        const updatedSkuRelation = sku.SkuRelation.map((relation: any) => {
          if (relation.SkuId === skuRelationId) {
            return {
              ...relation,
              Duration: duration,
              DurationError:
                duration == ""
                  ? "Required"
                  : Number(duration) < relation.CostIntervalMin
                    ? "Min " +
                      relation.CostIntervalCd +
                      " '" +
                      relation.CostIntervalMin +
                      "'"
                    : "",
            };
          }
          return relation;
        });
        return {
          ...sku,
          IsSelected: shouldAutoSelect ? true : sku.IsSelected,
          Quantity: shouldAutoSelect
            ? sku.Quantity === ""
              ? 1
              : sku.Quantity
            : sku.Quantity,
          QuantityError: shouldAutoSelect ? "" : sku.QuantityError,
          Duration:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? sku.CostIntervalMin && sku.CostIntervalMin != "0"
                ? sku.CostIntervalMin
                : "1"
              : sku.Duration,
          DurationError:
            shouldAutoSelect &&
            (sku.Duration === "" || sku.Duration === undefined)
              ? ""
              : sku.DurationError,
          SkuRelation: updatedSkuRelation,
        };
      }
      return sku;
    });
    setSkuListViewData(updatedSkuListViewData);

    const updatedSku = updatedSkuListViewData.filter(
      (sku: any) => sku.SkuId === skuId,
    );

    if (selectedSkuList.filter((sku: any) => sku.SkuId === skuId).length > 0) {
      const updatedSkuList = selectedSkuList.map((sku: any) => {
        if (sku.SkuId === skuId) {
          const updatedSkuRelation = sku.SkuRelation.map((relation: any) => {
            if (relation.SkuId === skuRelationId) {
              return {
                ...relation,
                Duration: duration,
                DurationError:
                  duration == ""
                    ? "Required"
                    : Number(duration) < relation.CostIntervalMin
                      ? "Min " +
                        relation.CostIntervalCd +
                        " '" +
                        relation.CostIntervalMin +
                        "'"
                      : "",
              };
            }
            return relation;
          });
          return { ...sku, SkuRelation: updatedSkuRelation };
        }
        return sku;
      });
      setSelectedSkuList(updatedSkuList);
    } else {
      setSelectedSkuList([...selectedSkuList, updatedSku].flat());
    }
  };

  const getCostInterval = (costInterval: string) => {
    if (costInterval == "month") {
      return "Per month";
    }
    if (costInterval == "onetime") {
      return "One time";
    }
    if (costInterval == "week") {
      return "Per week";
    }
    if (costInterval == "autotransact") {
      return "Auto Transact";
    }
  };

  const calculatePeriodTotalCost = (sku: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = sku.Quantity || 0;
    const cost = sku.CostMetricCost || 0;

    const totalCost = Number(quantity) * cost;
    const formattedCost = totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });

    if (sku.CostIntervalCd === "month") {
      return `${formattedCost}/Month`;
    }

    if (sku.CostIntervalCd === "week") {
      return `${formattedCost}/Week`;
    }

    if (sku.CostIntervalCd === "onetime") {
      return `${formattedCost}/One time`;
    }
    if (sku.CostIntervalCd === "autotransact") {
      return `${formattedCost}/Auto transact`;
    }

    return formattedCost;
  };

  const getUnitsUsedForPeriod = (
    costInterval: string,
    costMetric: string,
    quantity: number | undefined | "",
  ) => {
    let metricUnit = costMetric?.split("_")?.[1]?.trim();

    metricUnit = metricUnit ? metricUnitMapping[metricUnit] : "";

    const formattedMetricUnit =
      metricUnit.toLowerCase() === "gb"
        ? "GB"
        : metricUnit.charAt(0).toUpperCase() +
          metricUnit.slice(1).toLowerCase();

    if (costInterval == "month") {
      return formattedMetricUnit
        ? `${formattedMetricUnit}${quantity && quantity > 1 ? "s" : ""} per month`
        : "per month";
    }
    if (costInterval == "week") {
      return formattedMetricUnit
        ? `${formattedMetricUnit}${quantity && quantity > 1 ? "s" : ""} per week`
        : "per week";
    } else {
      return `${formattedMetricUnit}` || "";
    }
  };
  const getCostIntervalForCostMetric = (
    costInterval: string,
    costMetric: string,
  ) => {
    let metricUnit = costMetric?.split("_")?.[1]?.trim();
    metricUnit = metricUnit ? metricUnitMapping[metricUnit] : "";
    const formattedMetricUnit =
      metricUnit.toLowerCase() === "gb" ? "GB" : metricUnit;
    if (costInterval == "month") {
      return `Per ${formattedMetricUnit}`;
    }
    if (costInterval == "onetime") {
      return `One time`;
    }
    if (costInterval == "week") {
      return `Per ${formattedMetricUnit}`;
    }
    if (costInterval == "autotransact") {
      return "Auto Transact";
    }
  };

  const getDuration = (sku: any) => {
    if (sku.CostIntervalCd == "onetime") {
      return (
        <>
          <span>One time cost</span>
          <div className="table-content-normal">One time</div>
        </>
      );
    } else {
      return (
        <>
          <NumericInput
            id={sku.SkuId}
            value={sku.Duration}
            error={sku.DurationError}
            min={
              sku.CostIntervalMin && sku.CostIntervalMin !== "0"
                ? Number(sku.CostIntervalMin)
                : 1
            }
            max={
              sku.CostIntervalCd === "month"
                ? MAX_DURATION_MONTHS
                : sku.CostIntervalCd === "week"
                  ? MAX_DURATION_WEEKS
                  : undefined
            }
            placeholder={sku.CostIntervalCd === "month" ? "Months" : "Weeks"}
            disabled={
              sku.CostIntervalCd?.toLowerCase() ===
              COST_INTERVAL_CODE.auto_transact
            }
            onChange={(val) => handleDurationChange(sku.SkuId, val)}
            label="Duration"
            inputType="duration"
          />
          <div className="table-content-normal">
            {sku.CostIntervalCd === "month" ? "Months" : "Weeks"}
          </div>
        </>
      );
    }
  };

  const getRelationDuration = (sku: any, skuRelation: any) => {
    if (skuRelation.CostIntervalCd == "onetime") {
      return (
        <>
          <span>One time cost</span>
          <div className="table-content-normal">One time</div>
        </>
      );
    } else {
      return (
        <>
          <NumericInput
            id={skuRelation.SkuId}
            value={skuRelation.Duration}
            error={skuRelation.DurationError}
            min={
              skuRelation.CostIntervalMin && skuRelation.CostIntervalMin !== "0"
                ? Number(skuRelation.CostIntervalMin)
                : 1
            }
            max={
              skuRelation.CostIntervalCd === "month"
                ? MAX_DURATION_MONTHS
                : skuRelation.CostIntervalCd === "week"
                  ? MAX_DURATION_WEEKS
                  : undefined
            }
            placeholder={
              skuRelation.CostIntervalCd === "month" ? "Months" : "Weeks"
            }
            disabled={
              skuRelation.CostIntervalCd?.toLowerCase() ===
              COST_INTERVAL_CODE.auto_transact
            }
            onChange={(val) =>
              handleRelationDurationChange(sku.SkuId, skuRelation.SkuId, val)
            }
            label="Duration"
            inputType="duration"
          />
          <div className="table-content-normal">
            {skuRelation.CostIntervalCd === "month" ? "Months" : "Weeks"}
          </div>
        </>
      );
    }
  };

  const calculateTotalCost = (sku: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = sku.Quantity || 0;
    const cost = sku.CostMetricCost || 0;
    const duration = sku.Duration || 0;

    const totalCost = quantity * cost * duration;
    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const calculateRelationTotalCost = (sku: any, skuRelation: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = skuRelation.Quantity || 0;
    const cost = skuRelation.CostMetricCost || 0;
    const duration = skuRelation.Duration || 0;
    const totalCost = quantity * cost * duration;

    return totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const calculateRelationPeriodTotalCost = (sku: any, skuRelation: any) => {
    if (!sku.IsSelected) {
      return "--";
    }
    const quantity = skuRelation.Quantity || 0;
    const cost = skuRelation.CostMetricCost || 0;

    const totalCost = Number(quantity) * cost;
    const formattedCost = totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });

    if (skuRelation.CostIntervalCd === "month") {
      return `${formattedCost}/Month`;
    }

    if (skuRelation.CostIntervalCd === "week") {
      return `${formattedCost}/Week`;
    }

    if (skuRelation.CostIntervalCd === "onetime") {
      return `${formattedCost}/One time`;
    }
    if (skuRelation.CostIntervalCd === "autotransact") {
      return `${formattedCost}/Auto transact`;
    }

    return formattedCost;
  };

  const handleClick = async (item: ArtifactType) => {
    if (item.ArtifactTypeCd === "prodarttype_doc") {
      setFileLoading(true);
      const fileData = await dispatch(
        fetchArtifactFileData({
          productId: item.ProductId,
          productArtifactId: item.ProductArtifactId,
        }),
      );
      if (fileData && fileData.payload.Status === "success") {
        setFileLoading(false);
        downloadBase64File(
          fileData.payload.Data.fileContent,
          fileData.payload.Data.fileName,
          fileData.payload.Data.contentType,
        );
      } else {
        setFileLoading(false);
        setToastError("Something went wrong. Please try again.");
      }
    } else if (item.ArtifactTypeCd === "prodarttype_link") {
      window.open(urlify(item.URL), "_blank", "noopener,noreferrer");
    } else {
      setToastError("Something went wrong. Please try again.");
    }
  };

  const breadcrumbItems = [
    {
      label: "External Submission",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "External Submission",
      value: "Marketplace",
      elementKey: "submission",
      text: "Marketplace",
      url: `/marketplace`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "External Submission",
      value: "product name",
      elementKey: "submission",
      text: productData?.Data?.Name || "",
      url: `/product/${param.id}`,
      disabled: true,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  const uniqueID = useId();

  const handleProductSupportClick = (val: string | null | undefined) => {
    if (!val) {
      return;
    } else {
      if (isValidUrl(val)) {
        window.open(val, "_blank")?.focus();
      }
    }
  };

  const toastCloseHandler = () => {
    setToastError("");
  };

  useEffect(() => {
    if (!isViewDetailsOpen) return;
    const handlePointerDown = (e: any) => {
      const modalEl = document.querySelector(".product-detail-view-details");
      if (!modalEl) return;
      if (!modalEl.contains(e.target)) {
        // setIsViewDetailsOpen(false);
        handleCloseViewDetails();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isViewDetailsOpen]);

  const mapArtifactCategory = (ArtifactCategoryCd: string) => {
    let category = "";
    if (ArtifactCategoryCd === "prodartcatg_delivery") {
      category = "Delivery";
    } else if (ArtifactCategoryCd === "prodartcatg_learning") {
      category = "Learning";
    } else if (ArtifactCategoryCd === "prodartcatg_marketing") {
      category = "Marketing & Enablement";
    } else if (ArtifactCategoryCd === "prodartcatg_related") {
      category = "Related Materials";
    } else if (ArtifactCategoryCd === "prodartcatg_success") {
      category = "Success Cases";
    } else {
      category = ArtifactCategoryCd;
    }
    return category;
  };

  if (!isEmpty(errorProductData)) {
    if (errorProductData?.includes("404")) {
      return <NotFound />;
    } else {
      return (
        <ErrorOrderPage
          image={productListEmptyImg} // replace with actual image path
          header={ERROR_RETRIVING_DATA}
          handleRefresh={resetPage}
        />
      );
    }
  }

  return (
    <div className="product-detail-wrapper">
      {isProductDataLoading &&
        isSkuInformationLoading &&
        isSkuListLoading &&
        isDraftOrdersListLoading && <Loader />}
      {errorProductData && <div className="error">{errorProductData}</div>}
      {productData && productData !== null && (
        <>
          <section className="product-content-header commercial-hub-header-banner-lg">
            <div className="product-cost-setup-bredcrumb">
              <BreadcrumbHome
                items={breadcrumbItems}
                ariaLabel="Breadcrumb navigation"
              />
            </div>
            <h1 className="product-title">{productData?.Data?.Name}</h1>
            <div className="product-description">
              {productData?.Data?.Description &&
              productData?.Data?.Description.length > 250 ? (
                <MotifTooltip
                  contentClassName="tooltip-content"
                  allowClick
                  allowHover
                  id="tooltip-header-banner"
                  placement="auto"
                  trigger={productData?.Data?.Description.slice(0, 250).concat(
                    " ...",
                  )}
                  usePortal
                  variant="default"
                >
                  {productData?.Data?.Description}
                </MotifTooltip>
              ) : (
                productData?.Data?.Description
              )}
            </div>
          </section>
          <section className="product-tab-content">
            <MotifTabNavigation
              defaultActiveKey={0}
              orientation="horizontal"
              size="md"
              variant="default"
            >
              <MotifTabControl onClick={() => onTabChange(0)}>
                {OVERVIEW}
              </MotifTabControl>
              <MotifTabControl onClick={() => onTabChange(1)}>
                {PRODUCT_SKUS_INFORMATION}
              </MotifTabControl>
              <MotifTabControl onClick={() => onTabChange(2)}>
                {MORE_INFORMATION}
              </MotifTabControl>
              <MotifTabControl
                className={`${artifactData && artifactData?.Data && artifactData?.Data?.length > 0 ? "" : "hide"}`}
                onClick={() => onTabChange(3)}
              >
                {RESOURCES}
              </MotifTabControl>
              <MotifTabControl onClick={() => onTabChange(4)}>
                {CONTACTS}
              </MotifTabControl>
            </MotifTabNavigation>
          </section>
          <section className="product-content-body">
            {selectedTab === 0 && (
              <div className="product-content-body-ct-marketplace">
                <div className="description-div">
                  <span className="description">Commercial description </span>
                  <div
                    ref={descriptionRef}
                    className={`product-commercial-description no-padding ql-editor${isDescriptionExpanded ? "" : " description-clamped"}`}
                    dangerouslySetInnerHTML={
                      productData?.Data?.CommercialDescription
                        ? {
                            __html:
                              productData?.Data?.CommercialDescription.replace(
                                /&nbsp;/g,
                                " ",
                              ),
                          }
                        : undefined
                    }
                  ></div>
                  {showReadMore && (
                    <div
                      className="read-more-div"
                      onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                    >
                      {isDescriptionExpanded ? "Read less" : "Read more"}
                    </div>
                  )}
                </div>
                <div className="offerings-div">Available offerings</div>
                <p>
                  The list below contains the available offerings for{" "}
                  {productData?.Data?.Name}
                </p>
                <div className="sku-list">
                  {isSkuListLoading && <Loader />}
                  {errorSkuList && <div className="error">{errorSkuList}</div>}
                  {
                    <>
                      <div className="sku-list-header">
                        <div>
                          <SearchInput
                            id={`sku-search-product-details- ${uniqueID}`}
                            aria-label={"Search"}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={(e: any) => {
                              const isContainEmoji = containsEmoji(
                                e.target.value,
                              );
                              if (isContainEmoji) {
                                setSearchValidationError(EMOJI_ERROR_MESSAGE);
                              } else {
                                setSearchValidationError("");
                                handleSearch(e.target.value);
                              }

                              if (e.target.value === "") {
                                setPaginationObj({
                                  ...paginationObj,
                                  searchText: "",
                                  currentPage: 1,
                                  pageSize: 10,
                                });
                              }
                            }}
                            clearSearch={() => {
                              setSearchValidationError("");
                              setPaginationObj({
                                ...paginationObj,
                                searchText: "",
                                currentPage: 1,
                                pageSize: 10,
                              });
                            }}
                            onEnter={(
                              e: React.KeyboardEvent<HTMLInputElement>,
                            ) => {
                              const isContainEmoji = containsEmoji(
                                e.currentTarget.value,
                              );
                              if (!isContainEmoji) {
                                handleSearch(e.currentTarget.value);
                              }
                            }}
                            placeholder={"Search by SKU name or SKU ID"}
                            value={paginationObj.searchText}
                            className="sku-search-input"
                          />
                          <div>
                            {searchValidationError && (
                              <MotifErrorMessage className="search-helper-text">
                                {searchValidationError}
                              </MotifErrorMessage>
                            )}
                          </div>
                        </div>
                        <div className="sku-list-header-btns">
                          <span
                            className={
                              selectedSkuList.length == 0
                                ? "sku-cancel-selection-disabled"
                                : "sku-cancel-selection"
                            }
                            onClick={handleCancelSelection}
                          >
                            Clear selection
                          </span>
                          <MotifButton
                            //  className="sku-add-to-order-bundle-btn"
                            variant="secondary"
                            size="medium"
                            onClick={handleAddToOrderBundle}
                            disabled={selectedSkuList.length == 0}
                          >
                            Add to order{" "}
                            {selectedSkuList.length > 0
                              ? `(${selectedSkuList.length})`
                              : ""}
                            <MotifIcon
                              iconFunction={IconoirArrowRight}
                              size="24"
                              title="Add to Order"
                            />
                          </MotifButton>
                        </div>
                      </div>
                      {skuListViewData.length == 0 &&
                        paginationObj.searchText && (
                          <div className="no-search-container">
                            <NoSearchResult
                              searchValue={paginationObj.searchText}
                            />
                          </div>
                        )}
                      <div className="table-wrapper">
                        {skuListViewData.length > 0 && (
                          <table className="motif-table-standard">
                            <thead className="sticky-header">
                              <tr>
                                <th className="overview-select">
                                  <MotifCheckbox
                                    id="checkbox-select-all"
                                    name="checkbox-select-all"
                                    disabled={
                                      skuListViewData.filter(
                                        (sku: any) =>
                                          sku.CostIntervalCd?.toLowerCase() !==
                                          COST_INTERVAL_CODE.auto_transact,
                                      ).length === 0
                                    }
                                    checked={
                                      skuListViewData.filter(
                                        (sku: any) =>
                                          sku.CostIntervalCd?.toLowerCase() !==
                                          COST_INTERVAL_CODE.auto_transact,
                                      ).length > 0 &&
                                      skuListViewData
                                        .filter(
                                          (sku: any) =>
                                            sku.CostIntervalCd?.toLowerCase() !==
                                            COST_INTERVAL_CODE.auto_transact,
                                        )
                                        .every((sku: any) => sku.IsSelected)
                                    }
                                    onChange={handleSelectAllChange}
                                  >
                                    {""}
                                  </MotifCheckbox>
                                </th>
                                <th className="overview-sku-name">
                                  <OrderItemsHeaderTooltip
                                    label="SKU name/SKU ID"
                                    type="sku"
                                  />
                                </th>
                                <th className="overview-unit-cost">
                                  <OrderItemsHeaderTooltip
                                    label="Unit cost"
                                    type="unitCost"
                                  />
                                </th>
                                <th className="overview-quantity">
                                  <OrderItemsHeaderTooltip
                                    label="Units per period"
                                    type="unitsPerPeriod"
                                  />
                                </th>
                                <th className="overview-duration">
                                  <OrderItemsHeaderTooltip
                                    label="No. of periods"
                                    type="numberOfPeriods"
                                  />
                                </th>
                                <th className="overview-total-cost">
                                  <OrderItemsHeaderTooltip
                                    label="Total cost"
                                    type="totalCost"
                                  />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {skuListViewData.length > 0 &&
                                skuListViewData.map((sku: any) => {
                                  if (sku.SkuRelation.length === 0) {
                                    return (
                                      <tr
                                        key={sku.SkuId}
                                        className={
                                          sku.IsSelected
                                            ? "selected-sku-row"
                                            : ""
                                        }
                                      >
                                        <td className="checkbox-cell">
                                          <div className="table-cell">
                                            <CheckBoxToolTip
                                              isDisabled={
                                                sku.CostIntervalCd.toLowerCase() ===
                                                COST_INTERVAL_CODE.auto_transact
                                              }
                                              checked={sku.IsSelected}
                                              onChange={() =>
                                                handleSelectChange(sku.SkuId)
                                              }
                                              checkboxClassName="product-detail-checkbox"
                                            />
                                          </div>
                                        </td>
                                        <td className="sku-cell">
                                          <div className="product-details-sku-name-responsive">
                                            <MotifTruncate
                                              className="product-details-sku-truncate-responsive"
                                              maxLines={1}
                                            >
                                              {sku.Name}
                                            </MotifTruncate>
                                          </div>
                                          <div className="sku-number">
                                            <span>{sku.SkuId}</span>
                                            <MotifTooltip
                                              contentClassName="tooltip-content"
                                              placement="top"
                                              hideCloseButton
                                              allowHover
                                              allowClick={false}
                                              trigger={
                                                <MotifChip
                                                  className={`sku-badge ${sku.CostMetricCd.includes("var_") ? "variable" : "fixed"}`}
                                                  onClick={() => {}}
                                                  disabled={false}
                                                >
                                                  {sku.CostMetricCd.includes(
                                                    "var_",
                                                  )
                                                    ? "Variable"
                                                    : "Fixed"}
                                                </MotifChip>
                                              }
                                              variant="default"
                                            >
                                              <div className="label-info-icon-text">
                                                {sku.CostMetricCd.includes(
                                                  "var_",
                                                )
                                                  ? variableCostInfo()
                                                  : fixedCostInfo()}
                                              </div>
                                            </MotifTooltip>
                                          </div>
                                          <div className="view-details-link">
                                            <span
                                              onClick={() =>
                                                handleViewDetails(
                                                  sku.SkuId,
                                                  sku.Name,
                                                  sku.Description,
                                                  sku.StatusName,
                                                  sku.CostMetricCost,
                                                  sku.IsReturnable,
                                                  sku.CostIntervalCd,
                                                  sku.CostMetricNSR || 0,
                                                  sku.CostMetricCd,
                                                  sku.CostMacroCategoryCd,
                                                  sku.Indexation,
                                                )
                                              }
                                            >
                                              View Details
                                            </span>
                                            <span>
                                              <MotifIcon
                                                iconFunction={IconoirArrowRight}
                                                size="20"
                                                fill="#55A6FC"
                                                title="View Details"
                                              />
                                            </span>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="text-right-align">
                                            {sku.CostMetricCost.toLocaleString(
                                              "en-US",
                                              {
                                                style: "currency",
                                                currency: "USD",
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2,
                                              },
                                            )}
                                          </div>
                                          <div className="table-content-normal text-right-align">
                                            {getCostIntervalForCostMetric(
                                              sku.CostIntervalCd,
                                              sku.CostMetricCd,
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          {isFixedCostSku(sku) ? (
                                            <div className="quantity">1</div>
                                          ) : (
                                            <>
                                              <NumericInput
                                                id={sku.SkuId}
                                                value={sku.Quantity}
                                                error={sku.QuantityError}
                                                min={0}
                                                max={MAX_QUANTITY}
                                                placeholder="Qty"
                                                disabled={
                                                  sku.CostIntervalCd?.toLowerCase() ===
                                                  COST_INTERVAL_CODE.auto_transact
                                                }
                                                onDecrementAtOne={() =>
                                                  handleSelectChange(sku.SkuId)
                                                }
                                                onChange={(val) =>
                                                  handleQuantityChange(
                                                    sku.SkuId,
                                                    val,
                                                  )
                                                }
                                                label="Quantity"
                                                inputType="quantity"
                                              />
                                            </>
                                          )}
                                          <div className="table-content-normal">
                                            {getUnitsUsedForPeriod(
                                              sku.CostIntervalCd,
                                              sku.CostMetricCd,
                                              sku.Quantity,
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <div>{getDuration(sku)}</div>
                                        </td>
                                        <td className="total-cost-cell text-right-align">
                                          {calculateTotalCost(sku)}
                                          <div className="table-content-normal">
                                            {calculatePeriodTotalCost(sku)}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  } else {
                                    return (
                                      <tr
                                        key={sku.SkuId}
                                        className={
                                          sku.IsSelected
                                            ? "selected-sku-row"
                                            : ""
                                        }
                                      >
                                        <td className="checkbox-cell">
                                          <div className="table-cell">
                                            <MotifCheckbox
                                              id="checkbox-example"
                                              name="checkbox-example"
                                              onChange={() =>
                                                handleSelectChange(sku.SkuId)
                                              }
                                              checked={sku.IsSelected}
                                              className="product-detail-checkbox"
                                            >
                                              {""}
                                            </MotifCheckbox>
                                          </div>
                                        </td>
                                        <td>
                                          <ul className="tree">
                                            <li className="tree-trigger">
                                              <div className="table-cell">
                                                <div className="product-details-sku-name-responsive">
                                                  <MotifTruncate
                                                    className="product-details-sku-truncate-responsive"
                                                    maxLines={1}
                                                  >
                                                    {sku.Name}
                                                  </MotifTruncate>
                                                </div>
                                                <div className="sku-number">
                                                  <span>{sku.SkuId}</span>
                                                  <MotifTooltip
                                                    placement="top"
                                                    hideCloseButton
                                                    flip
                                                    offset={[0, 8]}
                                                    allowHover
                                                    allowClick={true}
                                                    contentClassName="tooltip-content"
                                                    trigger={
                                                      <MotifChip
                                                        className={`sku-badge ${sku.CostMetricCd.includes("var_") ? "variable" : "fixed"}`}
                                                        onClick={() => {}}
                                                        disabled={false}
                                                      >
                                                        {sku.CostMetricCd.includes(
                                                          "var_",
                                                        )
                                                          ? "Variable"
                                                          : "Fixed"}
                                                      </MotifChip>
                                                    }
                                                    variant="default"
                                                  >
                                                    <div className="label-info-icon-text">
                                                      {sku.CostMetricCd.includes(
                                                        "var_",
                                                      )
                                                        ? variableCostInfo()
                                                        : fixedCostInfo()}
                                                    </div>
                                                  </MotifTooltip>
                                                </div>
                                                <div className="view-details-link">
                                                  <span
                                                    onClick={() =>
                                                      handleViewDetails(
                                                        sku.SkuId,
                                                        sku.Name,
                                                        sku.Description,
                                                        sku.StatusName,
                                                        sku.CostMetricCost,
                                                        sku.IsReturnable,
                                                        sku.CostIntervalCd,
                                                        sku.CostMetricNSR,
                                                        sku.CostMetricCd,
                                                        sku.CostMacroCategoryCd,
                                                        sku.Indexation,
                                                      )
                                                    }
                                                    className="view-details-link"
                                                  >
                                                    View Details
                                                  </span>
                                                  <span>
                                                    <MotifIcon
                                                      iconFunction={
                                                        IconoirArrowRight
                                                      }
                                                      size="20"
                                                      fill="#55A6FC"
                                                      title="View Details"
                                                    />
                                                  </span>
                                                </div>
                                              </div>
                                            </li>
                                            <ul>
                                              {sku.SkuRelation.map(
                                                (skuRelation: any) => {
                                                  return (
                                                    <li key={skuRelation.SkuId}>
                                                      <div className="table-cell">
                                                        <div className="product-details-sku-name-responsive">
                                                          <MotifTruncate
                                                            maxLines={1}
                                                            className="product-details-sku-truncate-responsive"
                                                          >
                                                            {skuRelation.Name}
                                                          </MotifTruncate>
                                                        </div>
                                                        <div className="sku-number">
                                                          <span>
                                                            {skuRelation.SkuId}
                                                          </span>
                                                          <MotifTooltip
                                                            placement="top"
                                                            hideCloseButton
                                                            flip
                                                            offset={[0, 8]}
                                                            allowHover
                                                            allowClick={true}
                                                            contentClassName="tooltip-content"
                                                            trigger={
                                                              <MotifChip
                                                                className={`sku-badge ${skuRelation.CostMetricCd.includes("var_") ? "variable" : "fixed"}`}
                                                                onClick={() => {}}
                                                                disabled={false}
                                                              >
                                                                {skuRelation.CostMetricCd.includes(
                                                                  "var_",
                                                                )
                                                                  ? "Variable"
                                                                  : "Fixed"}
                                                              </MotifChip>
                                                            }
                                                            variant="default"
                                                          >
                                                            <div className="label-info-icon-text">
                                                              {skuRelation.CostMetricCd.includes(
                                                                "var_",
                                                              )
                                                                ? variableCostInfo()
                                                                : fixedCostInfo()}
                                                            </div>
                                                          </MotifTooltip>
                                                        </div>
                                                        <div className="view-details-link">
                                                          <span
                                                            onClick={() =>
                                                              handleViewDetails(
                                                                skuRelation.SkuId,
                                                                skuRelation.Name,
                                                                skuRelation.Description,
                                                                skuRelation.StatusName,
                                                                skuRelation.CostMetricCost,
                                                                skuRelation.IsReturnable,
                                                                skuRelation.CostIntervalCd,
                                                                skuRelation?.CostMetricNSR ||
                                                                  0,
                                                                sku.CostMetricCd,
                                                                sku.CostMacroCategoryCd,
                                                                skuRelation.Indexation,
                                                              )
                                                            }
                                                          >
                                                            View Details
                                                          </span>
                                                          <span>
                                                            <MotifIcon
                                                              iconFunction={
                                                                IconoirArrowRight
                                                              }
                                                              size="20"
                                                              fill="#55A6FC"
                                                              title="View Details"
                                                            />
                                                          </span>
                                                        </div>
                                                      </div>
                                                    </li>
                                                  );
                                                },
                                              )}
                                            </ul>
                                          </ul>
                                        </td>
                                        <td className="amount-col">
                                          <div className="table-cell text-right-align">
                                            <div>
                                              {sku.CostMetricCost.toLocaleString(
                                                "en-US",
                                                {
                                                  style: "currency",
                                                  currency: "USD",
                                                  maximumFractionDigits: 2,
                                                  minimumFractionDigits: 2,
                                                },
                                              )}
                                            </div>
                                            <div className="table-content-normal text-right-align">
                                              {getCostIntervalForCostMetric(
                                                sku.CostIntervalCd,
                                                sku.CostMetricCd,
                                              )}
                                            </div>
                                          </div>
                                          {sku.SkuRelation.map(
                                            (skuRelation: any) => {
                                              return (
                                                <div
                                                  className="table-cell text-right-align"
                                                  key={skuRelation.SkuId}
                                                >
                                                  <div>
                                                    {skuRelation.CostMetricCost.toLocaleString(
                                                      "en-US",
                                                      {
                                                        style: "currency",
                                                        currency: "USD",
                                                        maximumFractionDigits: 2,
                                                        minimumFractionDigits: 2,
                                                      },
                                                    )}
                                                  </div>
                                                  <div className="table-content-normal text-right-align">
                                                    {getCostIntervalForCostMetric(
                                                      skuRelation.CostIntervalCd,
                                                      skuRelation.CostMetricCd,
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            },
                                          )}
                                        </td>
                                        <td className="amount-col">
                                          <div className="table-cell">
                                            {isFixedCostSku(sku) ? (
                                              <div className="quntity">1</div>
                                            ) : (
                                              <>
                                                <NumericInput
                                                  id={sku.SkuId}
                                                  value={sku.Quantity}
                                                  error={sku.QuantityError}
                                                  min={0}
                                                  max={MAX_QUANTITY}
                                                  placeholder="Qty"
                                                  disabled={
                                                    sku.CostIntervalCd?.toLowerCase() ===
                                                    COST_INTERVAL_CODE.auto_transact
                                                  }
                                                  onDecrementAtOne={() =>
                                                    handleSelectChange(
                                                      sku.SkuId,
                                                    )
                                                  }
                                                  onChange={(val) =>
                                                    handleQuantityChange(
                                                      sku.SkuId,
                                                      val,
                                                    )
                                                  }
                                                  label="Quantity"
                                                  inputType="quantity"
                                                />
                                              </>
                                            )}
                                            <div className="table-content-normal">
                                              {getUnitsUsedForPeriod(
                                                sku.CostIntervalCd,
                                                sku.CostMetricCd,
                                                sku.Quantity,
                                              )}
                                            </div>
                                          </div>

                                          {sku.SkuRelation.map(
                                            (skuRelation: any) => {
                                              return (
                                                <div
                                                  className="table-cell"
                                                  key={skuRelation.SkuId}
                                                >
                                                  {isFixedCostSku(
                                                    skuRelation,
                                                  ) ? (
                                                    <div className="quntity">
                                                      1
                                                    </div>
                                                  ) : (
                                                    <>
                                                      <NumericInput
                                                        id={skuRelation.SkuId}
                                                        value={
                                                          skuRelation.Quantity
                                                        }
                                                        error={
                                                          skuRelation.QuantityError
                                                        }
                                                        min={0}
                                                        max={MAX_QUANTITY}
                                                        placeholder="Qty"
                                                        disabled={
                                                          skuRelation.CostIntervalCd?.toLowerCase() ===
                                                          COST_INTERVAL_CODE.auto_transact
                                                        }
                                                        onDecrementAtOne={() =>
                                                          handleSelectChange(
                                                            sku.SkuId,
                                                          )
                                                        }
                                                        onChange={(val) =>
                                                          handleRelationQuantityChange(
                                                            sku.SkuId,
                                                            skuRelation.SkuId,
                                                            val,
                                                          )
                                                        }
                                                        label="Quantity"
                                                        inputType="quantity"
                                                      />
                                                    </>
                                                  )}
                                                  <div className="table-content-normal">
                                                    {getUnitsUsedForPeriod(
                                                      skuRelation.CostIntervalCd,
                                                      skuRelation.CostMetricCd,
                                                      skuRelation.Quantity,
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            },
                                          )}
                                        </td>
                                        <td className="amount-col">
                                          <div className="table-cell">
                                            {getDuration(sku)}
                                          </div>
                                          {sku.SkuRelation.map(
                                            (skuRelation: any) => {
                                              return (
                                                <div
                                                  className="table-cell"
                                                  key={skuRelation.SkuId}
                                                >
                                                  {getRelationDuration(
                                                    sku,
                                                    skuRelation,
                                                  )}
                                                </div>
                                              );
                                            },
                                          )}
                                        </td>
                                        <td className="amount-col">
                                          <div className="table-cell total-cost-cell text-right-align">
                                            {calculateTotalCost(sku)}
                                            <div className="table-content-normal">
                                              {calculatePeriodTotalCost(sku)}
                                            </div>
                                          </div>
                                          {sku.SkuRelation.map(
                                            (skuRelation: any) => {
                                              return (
                                                <div
                                                  className="table-cell total-cost-cell text-right-align"
                                                  key={skuRelation.SkuId}
                                                >
                                                  {calculateRelationTotalCost(
                                                    sku,
                                                    skuRelation,
                                                  )}
                                                  <div className="table-content-normal">
                                                    {calculateRelationPeriodTotalCost(
                                                      sku,
                                                      skuRelation,
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            },
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  }
                                })}
                            </tbody>
                          </table>
                        )}
                      </div>
                      {skuListViewData.length > 0 && (
                        <CustomPagination
                          currentpage={paginationObj.currentPage}
                          pageSize={paginationObj.pageSize}
                          disableNext={false}
                          onChangePageSize={(page: number) => {
                            setPaginationObj({
                              ...paginationObj,
                              pageSize: Number(page),
                              currentPage: 1,
                            });
                          }}
                          onChangeNextPage={() => {
                            setPaginationObj({
                              ...paginationObj,
                              currentPage: paginationObj.currentPage + 1,
                            });
                          }}
                          onChangeFirstPage={() => {
                            setPaginationObj({
                              ...paginationObj,
                              currentPage: 1,
                            });
                          }}
                          onChangePreviousPage={() => {
                            setPaginationObj({
                              ...paginationObj,
                              currentPage: paginationObj.currentPage - 1,
                            });
                          }}
                          onChangeLastPage={() => {
                            setPaginationObj({
                              ...paginationObj,
                              currentPage: skuListData?.TotalPages || 0,
                            });
                          }}
                          pageSizeSelector={[10, 20, 50]}
                          totalRecords={skuListData?.TotalRecord || 0}
                        />
                      )}
                      {isCancelSelectionOpen && (
                        <MotifModal
                          show={isCancelSelectionOpen}
                          onClose={handleCancelSelectionContinue}
                          className="product-detail-cancel-selection"
                          size="lg"
                        >
                          <MotifModalHeader>
                            You have Unsaved Selections
                          </MotifModalHeader>
                          <MotifModalBody>
                            <div className="cancel-selection-content">
                              <p>
                                You have unsaved selections that will be lost if
                                you choose to cancel or leave this screen.
                              </p>
                              <p>Do you want to Continue?</p>
                            </div>
                          </MotifModalBody>
                          <MotifModalFooter>
                            <MotifButton
                              className="product-cancel-selection-confirm-btn"
                              onClick={handleCancelSelectionLeave}
                              variant="secondary"
                            >
                              Leave
                            </MotifButton>
                            <MotifButton
                              className="product-cancel-selection-close-btn"
                              onClick={handleCancelSelectionContinue}
                            >
                              Continue Editing
                            </MotifButton>
                          </MotifModalFooter>
                        </MotifModal>
                      )}
                    </>
                  }
                  {toastError !== "" && (
                    <MotifToast variant="error" onClose={toastCloseHandler}>
                      <div slot="toast-supporting-text">{toastError}</div>
                    </MotifToast>
                  )}
                </div>
              </div>
            )}
            {selectedTab === 1 && (
              <div className="product-content-body-skus">
                {errorSkuInformation && (
                  <div className="error">{errorSkuInformation}</div>
                )}
                {skuInformationData.Data.map((skuInformation) => (
                  <MotifAccordion
                    variant="functional"
                    className="sku-accordion"
                    useChevronIcon={true}
                    key={skuInformation.SkuId}
                  >
                    <MotifAccordionTrigger>
                      {skuInformation.Name}
                    </MotifAccordionTrigger>
                    <MotifAccordionContent>
                      <div
                        className="ql-editor no-padding"
                        dangerouslySetInnerHTML={
                          skuInformation?.Description
                            ? {
                                __html: skuInformation?.Description.replace(
                                  /&nbsp;/g,
                                  " ",
                                ),
                              }
                            : undefined
                        }
                      ></div>
                    </MotifAccordionContent>
                  </MotifAccordion>
                ))}
              </div>
            )}
            {selectedTab === 2 && (
              <div className="product-content-body-advice">
                <div
                  className="ql-editor no-padding"
                  dangerouslySetInnerHTML={
                    productData?.Data?.AssetAdvice
                      ? {
                          __html: productData.Data.AssetAdvice.replace(
                            /&nbsp;/g,
                            " ",
                          ),
                        }
                      : undefined
                  }
                ></div>
              </div>
            )}
            {selectedTab === 3 && (
              <>
                <div className="notification-grid">
                  {toastError !== "" && (
                    <MotifToast variant="error" onClose={toastCloseHandler}>
                      <div slot="toast-supporting-text">{toastError}</div>
                    </MotifToast>
                  )}
                  {!isEmpty(errorArtifactData) && (
                    <MotifToast variant="error" onClose={toastCloseHandler}>
                      <div slot="toast-supporting-text">
                        Issue in loading Product Artifacts
                      </div>
                    </MotifToast>
                  )}
                  {fileLoading && (
                    <div className="loader-grid">
                      <Loader show={fileLoading || isArtifactDataLoading} />
                    </div>
                  )}
                </div>
                <div className="grid-container">
                  {artifactData &&
                    artifactData !== null &&
                    artifactData.Data?.map((item: any) => (
                      <div className="card" key={item.ProductArtifactId}>
                        <div className="left">
                          <div className="contact-avatar">
                            <MotifAvatar
                              variant="icon"
                              size="large"
                              src={
                                item.ArtifactTypeCd === "prodarttype_doc"
                                  ? IconoirNotes
                                  : IconoirLink
                              }
                            />
                          </div>
                          <div className="text">
                            <h4>
                              <MotifTruncate maxLines={1} maxWidth={350}>
                                {item.Title}
                              </MotifTruncate>
                            </h4>
                            <p className="category">
                              {mapArtifactCategory(item.ArtifactCategoryCd)}
                            </p>
                            <p className="desc">{item.Description}</p>
                          </div>
                        </div>
                        <div className="right">
                          <MotifTooltip
                            contentClassName="tooltip-content"
                            allowHover
                            flip
                            id="tooltip-artifact"
                            placement="top"
                            style={{}}
                            trigger={
                              <div
                                className="contact-artifact-icon"
                                onClick={() => handleClick(item)}
                              >
                                <MotifIcon
                                  size="20"
                                  src={
                                    item.ArtifactTypeCd === "prodarttype_doc"
                                      ? IconoirDownload
                                      : IconoirOpenNewWindow
                                  }
                                />
                              </div>
                            }
                            usePortal
                            variant="default"
                            hideCloseButton
                          >
                            {item.ArtifactTypeCd === "prodarttype_doc"
                              ? `Download ${isEmpty(item.FileSize) ? "" : `(${formattingBytes(item.FileSize)})`}`
                              : "Open Link"}
                          </MotifTooltip>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
            {selectedTab === 4 && (
              <div className="product-content-body-contacts">
                <div className="motif-row">
                  <div className="motif-col-lg-12 motif-col-md-8 motif-col-sm-4">
                    <div className="contact-card contact-card-support">
                      <div>
                        <h4>Support Link</h4>
                        <div className="contact-wrapper">
                          {productData?.Data?.ProductSupport ? (
                            <>
                              <div className="contact-avatar">
                                <MotifAvatar variant="icon" src={IconoirUser} />
                              </div>
                              <div>
                                <div className="contact-name">
                                  Product Support
                                </div>
                                <div
                                  className="contact-support-link"
                                  onClick={() =>
                                    handleProductSupportClick(
                                      productData?.Data?.ProductSupport,
                                    )
                                  }
                                >
                                  {productData?.Data?.ProductSupportText &&
                                  productData?.Data?.ProductSupportText.trim() !==
                                    "" ? (
                                    <>{productData.Data.ProductSupportText}</>
                                  ) : (
                                    "Click here"
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="no-data">No data Available</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="motif-row">
                  <div className="motif-col-lg-12 motif-col-md-8 motif-col-sm-4">
                    <div className="contact-card contact-card-contact">
                      <div>
                        <h4>Product Contact</h4>
                        <div className="motif-row">
                          {productData?.Data?.ProductContact &&
                          productData?.Data?.ProductContact.length > 0 ? (
                            productData?.Data?.ProductContact.map((contact) => (
                              <div className="motif-col-lg-4 motif-col-md-8 motif-col-sm-4">
                                <div className="contact-wrapper">
                                  <div className="contact-avatar">
                                    <MotifAvatar
                                      description={
                                        contact.Name + " " + contact.Email
                                      }
                                      userName={contact.Name}
                                    />
                                  </div>
                                  <div>
                                    <div className="contact-name">
                                      {contact.Name}
                                    </div>
                                    <a
                                      className="contact-email-link"
                                      href={`mailto:${contact.Email}`}
                                    >
                                      {contact.Email}
                                    </a>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="motif-col-lg-12 no-data">
                              No data available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {isViewDetailsOpen && (
        <MotifModal
          show={isViewDetailsOpen}
          onClose={handleCloseViewDetails}
          className="product-detail-view-details"
          size="lg"
          variant="slideout"
        >
          <MotifModalHeader>View Details</MotifModalHeader>
          <MotifModalBody>
            <section>
              <h4 className="view-details-name">{viewDetailsData.skuName}</h4>
              <p className="view-details-id">{viewDetailsData.skuId}</p>
              <div className="section-divider"></div>
            </section>
            <section>
              <h4 className="view-details-description-header">Description</h4>
              <p className="view-details-description">
                <div
                  className="ql-editor no-padding"
                  dangerouslySetInnerHTML={
                    viewDetailsData?.skuDescription
                      ? {
                          __html: viewDetailsData?.skuDescription.replace(
                            /&nbsp;/g,
                            " ",
                          ),
                        }
                      : undefined
                  }
                ></div>
              </p>
              <div className="section-divider"></div>
            </section>
            <section>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Status</h4>
                <p className="view-details-common-value">
                  {viewDetailsData.skuCommercialStatus}
                </p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Cost</h4>
                <p className="view-details-common-value">
                  {viewDetailsData.skuCost.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Returnable</h4>
                <p className="view-details-common-value text-capitalize">
                  {viewDetailsData.returnable ? "Yes" : "No"}
                </p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Cost Interval</h4>
                <p className="view-details-common-value">
                  {getCostInterval(viewDetailsData.skuCostInterval)}
                </p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Cost Type</h4>
                <p className="view-details-common-value">{costType}</p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Cost Metric</h4>
                <p className="view-details-common-value">{costMetric}</p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">
                  {COST_MACRO_CATEGORY}
                </h4>
                <p className="view-details-common-value">{macroCategory}</p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">NSR</h4>
                <p className="view-details-common-value">
                  {viewDetailsData.skuCostMetricNSR
                    ? `$${viewDetailsData.skuCostMetricNSR}`
                    : "-"}
                </p>
              </div>
              <div className="view-details-common-content">
                <h4 className="view-details-common-header">Indexation</h4>
                <p className="view-details-common-value">
                  {viewDetailsData.SkuIndexation
                    ? `${viewDetailsData.SkuIndexation}`
                    : "-"}
                </p>
              </div>
            </section>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton
              className="product-view-details-close-btn"
              onClick={handleCloseViewDetails}
            >
              Close
            </MotifButton>
          </MotifModalFooter>
        </MotifModal>
      )}

      {isAddToOrderBundleOpen &&
        (draftOrdersListData == null || draftOrdersListData?.length == 0) && (
          <MotifModal
            show={isAddToOrderBundleOpen}
            onClose={handleAddToOrderBundleCancel}
            className="add-to-new-order-bundle-modal"
            size="lg"
          >
            <MotifModalHeader>Create New Order Bundle</MotifModalHeader>
            <MotifModalBody className="add-to-new-order-bundle-body">
              <div>
                <div className="body-title">Create New Order Bundle</div>
                <div className="body-content">
                  Create a new order bundle when form is submitted
                </div>
                <div className="body-input">
                  <MotifInput
                    className="add-to-order-bundle-input"
                    placeholder="Enter name of new order bundle"
                    value={orderBundleName}
                    onChange={(e) => {
                      if (isStringHavingSpecialChars(e.target.value)) {
                        return;
                      }
                      setOrderBundleName(e.target.value);
                      const isContainEmoji = containsEmoji(e.target.value);
                      if (e.target.value.trim() !== "") {
                        if (
                          draftOrdersListData &&
                          draftOrdersListData.filter(
                            (order) =>
                              order.Description === e.target.value.trim(),
                          ).length > 0
                        ) {
                          setNewOrderBundleError(
                            "This name is already taken, Please choose a different name",
                          );
                        } else if (isContainEmoji) {
                          setNewOrderBundleError(EMOJI_ERROR_MESSAGE_CREATE);
                        } else {
                          setNewOrderBundleError("");
                        }
                      } else {
                        setNewOrderBundleError(
                          "Please enter a valid order bundle name.",
                        );
                      }
                    }}
                  />
                  <div className="error-text-wrapper">
                    {newOrderBundleError && (
                      <span className="error-text">{newOrderBundleError}</span>
                    )}
                  </div>
                </div>
              </div>
            </MotifModalBody>
            <MotifModalFooter>
              <MotifButton
                className="product-cancel-selection-confirm-btn"
                onClick={handleAddToOrderBundleCancel}
                variant="secondary"
              >
                Cancel
              </MotifButton>
              <MotifButton
                className="product-view-details-close-btn"
                onClick={handleAddToOrderBundleConfirm}
                disabled={
                  orderBundleName.trim() == "" || newOrderBundleError !== ""
                }
              >
                Confirm
              </MotifButton>
            </MotifModalFooter>
          </MotifModal>
        )}

      {isAddToOrderBundleOpen &&
        draftOrdersListData != null &&
        draftOrdersListData?.length > 0 && (
          <MotifModal
            show={isAddToOrderBundleOpen}
            onClose={handleAddToOrderBundleCancel}
            className="add-to-order-bundle-modal"
            size="lg"
          >
            <MotifModalHeader>
              Select Existing Order Bundle or Create New
            </MotifModalHeader>
            <MotifModalBody>
              <div className="add-to-order-bundle-wrapper">
                <div
                  className={
                    isNewOrderBundle
                      ? "add-to-order-bundle-body selected-wrapper"
                      : "add-to-order-bundle-body"
                  }
                  onClick={selectNewOrderBundleOption}
                >
                  <div className="body-title">
                    <span>Create New Order Bundle</span>
                    {isNewOrderBundle && (
                      <span className="tick-icon">
                        <img
                          src={productPreviewTickIcon}
                          alt="Selected Order Bundle Option"
                        />
                      </span>
                    )}
                  </div>
                  <div className="body-content">
                    Create a new order bundle with the selected SKUs
                  </div>
                  <div className="body-input">
                    <MotifInput
                      className="add-to-order-bundle-input"
                      placeholder="Enter Order Bundle Name"
                      value={orderBundleName}
                      onFocus={selectNewOrderBundleOption}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Tab" && !e.shiftKey) {
                          selectExistingOrderBundleOption();
                        }
                      }}
                      onChange={(e) => {
                        if (isStringHavingSpecialChars(e.target.value)) {
                          return;
                        }
                        setOrderBundleName(e.target.value);
                        const isContainEmoji = containsEmoji(e.target.value);
                        if (e.target.value.trim() !== "") {
                          if (
                            draftOrdersListData &&
                            draftOrdersListData.filter(
                              (order) =>
                                order.Description === e.target.value.trim(),
                            ).length > 0
                          ) {
                            setNewOrderBundleError(
                              "This name is already taken, Please choose a different name",
                            );
                          } else if (isContainEmoji) {
                            setNewOrderBundleError(EMOJI_ERROR_MESSAGE_CREATE);
                          } else {
                            setNewOrderBundleError("");
                          }
                        } else {
                          setNewOrderBundleError(
                            "Please enter a valid order bundle name.",
                          );
                        }
                      }}
                    />
                    {newOrderBundleError && (
                      <span className="error-text">{newOrderBundleError}</span>
                    )}
                  </div>
                </div>
                <div className="add-to-order-bundle-divider"> or </div>
                <div
                  className={
                    isNewOrderBundle
                      ? "select-existing-order-bundle"
                      : "select-existing-order-bundle selected-wrapper"
                  }
                  onClick={selectExistingOrderBundleOption}
                  onFocusCapture={selectExistingOrderBundleOption}
                >
                  <div className="body-title">
                    <span>Select Existing Order Bundle</span>
                    {!isNewOrderBundle && (
                      <span className="tick-icon">
                        <img
                          src={productPreviewTickIcon}
                          alt="Selected Order Bundle Option"
                        />
                      </span>
                    )}
                  </div>
                  <div className="body-content">
                    Add the selected SKUs to an existing order bundle
                  </div>
                  <div className="body-input">
                    <MotifSelect
                      ariaLabelledBy="select-label"
                      visibleOptions={4}
                      onFocus={selectExistingOrderBundleOption}
                      onKeyDown={(e: any) => {
                        if (e.key === "Tab" && e.shiftKey) {
                          selectNewOrderBundleOption();
                        }
                      }}
                      onChange={(val) => {
                        handleSelectDraftOrderBundle(val);
                      }}
                      placeholder="Select Existing Order Bundle"
                      value={orderBundleDropdownValue}
                    >
                      {draftOrdersListData.map((order) => (
                        <MotifOption
                          key={order.OrderParentId}
                          value={order.Description}
                          isSelected={
                            orderBundleDropdownValue === order.Description
                          }
                        >
                          {order.Description}
                        </MotifOption>
                      ))}
                    </MotifSelect>
                    {existingOrderBundleError && (
                      <span className="error-text">
                        {existingOrderBundleError}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {!isNewOrderBundle && orderBundleDropdownValue && (
                <div className="body-preview-wrapper">
                  <span className="body-preview-title">Order Preview</span>
                  <div className="body-preview">
                    {orderBundleDropdownValue == "" && (
                      <div className="no-data">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="110"
                          height="111"
                          viewBox="0 0 110 111"
                          fill="none"
                        >
                          <path
                            d="M45.875 1C45.9699 1.01075 46.0517 1.02275 46.123 1.03809L46.2344 1.06152L46.4551 1.11816L46.5615 1.15137H46.5625L46.7725 1.22559L46.9014 1.27832C46.9867 1.3142 47.0577 1.3502 47.1162 1.38477L47.1387 1.39746L47.1611 1.40918L47.2334 1.44727C47.2716 1.47172 47.3059 1.49371 47.332 1.50977C47.3668 1.53113 47.3901 1.54522 47.4092 1.55762L47.5088 1.62988L47.5166 1.63574C47.5743 1.67664 47.6348 1.72274 47.6934 1.77051L47.7236 1.79492L47.7549 1.81738L47.832 1.87012C47.9048 1.93164 47.9713 1.99149 48.0371 2.05762L82.3408 36.5225L82.4365 36.6182L82.4619 36.6465L82.4814 36.6689L82.5029 36.6895C82.5772 36.7641 82.6483 36.8427 82.7158 36.9248L83.0488 36.6504L83.0674 36.6455L82.7119 36.9395C83.2389 37.5784 83.5576 38.3981 83.5576 39.2959V58.9785L84.3779 59.1289L84.5039 59.1514C94.4032 61.2835 101.825 70.1266 101.825 80.7139C101.825 85.2162 100.484 89.3981 98.2383 92.7998L97.7891 93.4795L98.3643 94.0566L108.027 103.761L108.023 103.765C108.63 104.425 109 105.307 109 106.279C109 108.339 107.341 110 105.302 110C104.341 110 103.468 109.632 102.811 109.03L93.1416 99.3164L92.5166 99.9365L91.9922 99.1182L91.9092 99.1689L90.832 99.8311L90.8389 99.8379C87.6176 101.722 83.8766 102.802 79.8818 102.802H4.68359C2.65268 102.794 1.00002 101.134 1 99.0811V4.69531C1.01412 2.71657 2.55881 1.11086 4.49512 1.00586L4.68359 1H45.875ZM8.39648 9.40332L8.39258 94.3701V95.3701H61.3516V94.4756L62.2021 93.8369C59.4988 90.1728 57.9024 85.6359 57.9023 80.7188C57.9023 70.1293 65.3271 61.2823 75.3799 59.123L76.1699 58.9531V43.0166H45.416C43.377 43.0164 41.7178 41.3559 41.7178 39.2959V8.40332H8.39648V9.40332ZM49.1104 35.5762H70.9268L69.2305 33.8701L50.8193 15.3691L49.1104 13.6514V35.5762ZM94.4336 80.7217C94.4236 72.652 87.9087 66.0999 79.8652 66.0898H79.8643C71.8152 66.0898 65.2942 72.6474 65.2939 80.7227C65.2939 88.7981 71.815 95.3564 79.8643 95.3564C87.9133 95.3562 94.4336 88.7979 94.4336 80.7227V80.7217Z"
                            fill="#4E4E5D"
                            stroke="#23232F"
                            stroke-width="2"
                          />
                        </svg>
                        <div className="no-data-text">
                          No preview available for the same
                        </div>
                      </div>
                    )}
                    {orderBundleDropdownValue != "" && (
                      <>
                        <div className="bundle-card">
                          <div className="card-subheader">
                            <div className="sub-title">
                              {orderBundleDropdownValue}
                            </div>
                            <div className="sub-title-description">
                              Products and services under this order bundle
                            </div>
                          </div>
                          <table className="motif-table-standard">
                            <thead>
                              <tr>
                                <th className="sku-th">SKU#/ SKU NAME</th>
                                <th>Quantity</th>
                                <th>Unit Cost</th>
                                <th>Duration</th>
                                <th>Total Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {draftOrdersListData
                                ?.filter(
                                  (order) =>
                                    order.Description ===
                                    orderBundleDropdownValue,
                                )[0]
                                .Details.map((order) => (
                                  <BundleCard
                                    key={order.ProductName}
                                    productName={order.ProductName}
                                    skuDetails={order.SkuDetails}
                                    order={order}
                                  />
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </MotifModalBody>
            <MotifModalFooter>
              <MotifButton
                className="product-cancel-selection-confirm-btn"
                onClick={handleAddToOrderBundleCancel}
                variant="secondary"
              >
                Cancel
              </MotifButton>
              <MotifButton
                className="add-to-bundle-confirm-btn"
                onClick={handleAddToOrderBundleConfirm}
                disabled={
                  (isNewOrderBundle && orderBundleName === "") ||
                  (isNewOrderBundle &&
                    draftOrdersListData &&
                    draftOrdersListData.filter(
                      (order) => order.Description === orderBundleName.trim(),
                    ).length > 0) ||
                  (!isNewOrderBundle && orderBundleDropdownValue === "") ||
                  (!isNewOrderBundle && existingOrderBundleError !== "") ||
                  newOrderBundleError !== ""
                }
              >
                Add to Order
              </MotifButton>
            </MotifModalFooter>
          </MotifModal>
        )}
    </div>
  );
};

export default ProductDetail;
